import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Form, Row, Col, Select, Input } from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ScheduleOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import axiosInstance from '../../Utils/axiosInstance'
import { CreateFeeAllocationData, DeleteFeeAllocationData, GetFeeAllocationData, GetFeeData, UpdateFeeAllocationData } from '../../API\'s/AdminApi'

const { Option } = Select

export default function FeeAllocation() {
  const [data, setData] = useState([])
  const [fee, setFee] = useState([])
  const [searchText, setSearchText] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [editId, setEditId] = useState(null)

  const [tableLoading, setTableLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
   const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  const [form] = Form.useForm()

  // ---------------- GET DATA ----------------
  const getFeeAllocation = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetFeeAllocationData)
      setData(res.data || [])
    } catch {
      toast.error('Failed to load fee data')
    } finally {
      setTableLoading(false)
    }
  }

  // ---------------- GET FEE ----------------
  const getFee = async () => {
    try {
      const res = await axiosInstance.get(GetFeeData)
      setFee(res.data || [])
    } catch {
      toast.error('Failed to load fee')
    }
  }

  useEffect(() => {
    getFeeAllocation()
    getFee()
  }, [])

  // ---------------- SUBMIT (CREATE + UPDATE) ----------------
  const onFinish = async (values) => {
    try {
      setSubmitLoading(true)

      const payload = {
        feeId: values.feeType,
        ageBelow15Amt: Number(values.below15),
        ageAbove15Amt: Number(values.above15),
      }

      let res

      if (editId) {
        res = await axiosInstance.put(UpdateFeeAllocationData, {
          id: editId,
          ...payload,
        })

        if (res.data === 'UPDATED') {
          toast.success('Fee updated successfully')
        }
      } else {
        res = await axiosInstance.post(CreateFeeAllocationData, payload)

        if (res.data === 'SAVED') {
          toast.success('Fee saved successfully')
        }
      }

      form.resetFields()
      setShowForm(false)
      setEditId(null)
      getFeeAllocation()
    } catch {
      toast.error('Operation failed')
    } finally {
      setSubmitLoading(false)
    }
  }

  // ---------------- DELETE ----------------
  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(`${DeleteFeeAllocationData}?id=${deleteId}`)
      toast.success('Deleted successfully')
      setDeleteId(null)
      getFeeAllocation()
    } catch {
      toast.error('Delete failed')
    }
  }

  // ---------------- TABLE ----------------
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Fee',
      dataIndex: 'feeName',
    },
    {
      title: 'Fee (Age below 15)',
      dataIndex: 'ageBelow15Amt',
    },
    {
      title: 'Fee (Age above 15)',
      dataIndex: 'ageAbove15Amt',
    },
    {
      title: 'Action',
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            style={{ marginRight: 8 }}
            onClick={() => {
              setEditId(record.id)
              setShowForm(true)

              form.setFieldsValue({
                feeType: record.feeId,
                below15: record.ageBelow15Amt,
                above15: record.ageAbove15Amt,
              })
            }}
          />

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setDeleteId(record.id)}
          />
        </>
      ),
    },
  ]

  // ---------------- SEARCH ----------------
  const filteredData = data.filter((item) =>
    item.feeName?.toLowerCase().includes(searchText.toLowerCase()),
  )

  return (
    <div>
      {/* TABLE */}
      {!showForm && (
        <>
          <div className="page-background p-2 p-md-4">
            <div className="page-header">
              <h2>
                <ScheduleOutlined style={{ marginRight: 8 }} />
                Fee Allocation
              </h2>
            </div>

            <Row gutter={16} className="pb-2 m-0">
              <Col xs={24} md={6} xl={15}></Col>

              <Col xs={24} md={12} xl={6}>
                <Input
                  placeholder="Search here"
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Col>

              <Col xs={24} md={6} xl={3}>
                <button
                  className="btn-aqua"
                  onClick={() => {
                    setShowForm(true)
                    setEditId(null)
                    form.resetFields()
                  }}
                >
                  <PlusOutlined /> Add Fee
                </button>
              </Col>
            </Row>

            <Table
              className="custom-table-header shadow"
              columns={columns}
              dataSource={filteredData}
              rowKey="id"
              loading={tableLoading}
              bordered
              style={{ overflowX: 'auto', backgroundColor: 'white' }}
              pagination={{
                showSizeChanger: true, // enable page size dropdown
                pageSizeOptions: ['5', '10', '20', '50'],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </div>
        </>
      )}

      {/* FORM */}
      {showForm && (
        <div className="row p-0 m-0">
          <div className="col-12 col-md-1 "></div>

          <div className="col-12 col-md-10  page-background1 p-2 p-md-4">
            <div className="page-header">
              <h2>
                <ScheduleOutlined style={{ marginRight: 8 }} />
                Fee Allocation
              </h2>
            </div>

            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Row gutter={16} >
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Fee"
                    name="feeType"
                    rules={[{ required: true, message: 'Select fee' }]}
                  >
                    <Select placeholder="Select Fee">
                      {fee.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.feeName}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item
                    label="Fee for Age below 15"
                    name="below15"
                    rules={[
                      { required: true, message: 'Enter fee' },
                      {
                        pattern: /^[0-9]+$/,
                        message: 'Only numbers allowed',
                      },
                    ]}
                  >
                    <Input placeholder="Enter fee for below 15" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                  <Form.Item
                    label="Fee for Age above 15"
                    name="above15"
                    rules={[
                      { required: true, message: 'Enter fee' },
                      {
                        pattern: /^[0-9]+$/,
                        message: 'Only numbers allowed',
                      },
                    ]}
                  >
                    <Input placeholder="Enter fee for above 15" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitLoading}
                >
                  {editId ? 'Update' : 'Submit'}
                </Button>

                <Button
                  style={{ marginLeft: 10 }}
                  onClick={() => {
                    setShowForm(false)
                    setEditId(null)
                    form.resetFields()
                  }}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          </div>

          <div className="col-12 col-md-1 "></div>
        </div>
      )}

      {/* DELETE MODAL */}
      <Modal
        title="Confirm Delete"
        open={deleteId !== null}
        onOk={confirmDelete}
        onCancel={() => setDeleteId(null)}
      >
        Are you sure you want to delete this fee?
      </Modal>
    </div>
  )
}